import React from 'react';
import styled from 'styled-components';
import { system, extractSysProps } from './system';

interface StyledCardInnerProps {
  $sys?: Record<string, any>;
  $shadow?: boolean;
}

// molekule Card: background white, box-shadow (if shadow=true), border-radius from theme.radius,
// overflow hidden, 1px solid border using theme.colors.greyLight
const StyledCardBase = styled.div<StyledCardInnerProps>`
  background: white;
  box-shadow: ${(p) =>
    p.$shadow
      ? p.theme && p.theme.shadow && p.theme.shadow.soft
        ? p.theme.shadow.soft
        : '0px 2px 16px rgba(27, 32, 43, 0.1)'
      : 'none'};
  border-radius: ${(p) => (p.theme && p.theme.radius != null ? p.theme.radius : 8)}px;
  overflow: hidden;
  border: 1px solid
    ${(p) => (p.theme && p.theme.colors && p.theme.colors.greyLight ? p.theme.colors.greyLight : '#B5BAC8')};
  ${(p) => system(p.$sys, p.theme)}
`;

// Card sub-components — layout matches molekule Card.js exactly:
// Footer: padding 16px
// Body: padding 16px; & + Footer { padding-top: 0px }
// Header: padding 16px; font-weight 700; & + Body { padding-top: 0px }
const StyledCardFooter = styled.div<{ $sys?: Record<string, any> }>`
  padding: 16px;
  ${(p) => system(p.$sys, p.theme)}
`;

const StyledCardBody = styled.div<{ $sys?: Record<string, any> }>`
  padding: 16px;

  & + ${StyledCardFooter} {
    padding-top: 0px;
  }

  ${(p) => system(p.$sys, p.theme)}
`;

const StyledCardHeader = styled.div<{ $sys?: Record<string, any> }>`
  padding: 16px;
  font-weight: 700;

  & + ${StyledCardBody} {
    padding-top: 0px;
  }

  ${(p) => system(p.$sys, p.theme)}
`;

export interface CardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  shadow?: boolean;
  children?: React.ReactNode;
  [key: string]: any;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  function Card(props, ref) {
    const { shadow, ...remaining } = props;
    const { sys, rest } = extractSysProps(remaining);
    return <StyledCardBase ref={ref} $shadow={!!shadow} $sys={sys} {...rest} />;
  },
);

// Sub-components
interface CardSubProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  children?: React.ReactNode;
  [key: string]: any;
}

const CardHeader = React.forwardRef<HTMLDivElement, CardSubProps>(
  function CardHeader(props, ref) {
    const { sys, rest } = extractSysProps(props as Record<string, any>);
    return <StyledCardHeader ref={ref} $sys={sys} {...rest} />;
  },
);

const CardBody = React.forwardRef<HTMLDivElement, CardSubProps>(
  function CardBody(props, ref) {
    const { sys, rest } = extractSysProps(props as Record<string, any>);
    return <StyledCardBody ref={ref} $sys={sys} {...rest} />;
  },
);

const CardFooter = React.forwardRef<HTMLDivElement, CardSubProps>(
  function CardFooter(props, ref) {
    const { sys, rest } = extractSysProps(props as Record<string, any>);
    return <StyledCardFooter ref={ref} $sys={sys} {...rest} />;
  },
);

(Card as any).Header = CardHeader;
(Card as any).Body = CardBody;
(Card as any).Footer = CardFooter;

export default Card as typeof Card & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};
